import type { TournamentsQueryVariables } from '@swf/generated/graphql/poker/MTT/operations';
import type { Site } from '@swf/generated/graphql/poker/MTT/schemas';

import { FilterName, type FiltersConfig } from '../../types/MTT';

export const GQL_VARIABLES_DEFAULT: TournamentsQueryVariables = {
  include: {
    sites: ['COM'] as Site[],
    flags2: ['HTML5_ALLOWED'],
    currencies: []
  }
};

export const GQL_VARIABLES_UPCOMING_ONLY: TournamentsQueryVariables = {
  include: {
    ...GQL_VARIABLES_DEFAULT.include,
    startsIn: 'H_24',
    statuses: ['PUBLISHED', 'SEATING', 'RUNNING', 'CANCELLED'],
    currencies: []
  },
  exclude: {
    currencies: ['PLAY'],
    flags1: ['DUMMY', 'SITANDGO']
  }
};

export const GQL_VARIABLES_UPCOMING_ONLY_PM: TournamentsQueryVariables = {
  include: { ...GQL_VARIABLES_UPCOMING_ONLY.include, currencies: ['PLAY'] },
  exclude: {
    currencies: [],
    flags1: ['DUMMY', 'SITANDGO']
  }
};

export const GQL_VARIABLES_SUBSCRIPTION: TournamentsQueryVariables = {
  include: { ...GQL_VARIABLES_DEFAULT.include, country: 'UK' },
  exclude: {
    currencies: ['PLAY'],
    flags1: ['DUMMY', 'SITANDGO']
  }
};
export const mappedFilterGamesFlatOptions = {
  name: FilterName.Games,
  options: [
    {
      id: 'games-holdem.nl',
      label: {
        defaultMessage: "No Limit Hold''em",
        id: 'TXTCLI_No_Limit_HoldXem',
        track: "No Limit Hold''em"
      },
      value: 'holdem.nl'
    },
    {
      id: 'games-omaha.pl',
      label: {
        defaultMessage: 'Pot Limit Omaha',
        id: 'TXTCLI_Pot_Limit_Omaha',
        track: 'Pot Limit Omaha'
      },
      value: 'omaha.pl'
    }
  ],
  title: { defaultMessage: 'Game', id: 'TXTCLI_Game', track: 'Game' }
};

export const mappedFilterGames = {
  name: FilterName.Games,
  hasGroups: true,
  options: [
    {
      id: 'games-holdem',
      label: {
        defaultMessage: "Hold''em Games",
        id: 'TXTCLI_HoldXem_Games',
        track: "Hold''em Games"
      },
      options: [
        {
          id: 'games-holdem-nl',
          label: {
            defaultMessage: "No Limit Hold''em",
            id: 'TXTCLI_No_Limit_HoldXem',
            track: "No Limit Hold''em"
          },
          value: 'holdem.nl'
        }
      ]
    },
    {
      id: 'games-omaha',
      label: {
        defaultMessage: 'Omaha Games',
        id: 'TXTCLI_Omaha_Games'
      },
      options: [
        {
          id: 'games-omaha-pl',
          label: {
            defaultMessage: 'Pot Limit Omaha',
            id: 'TXTCLI_Pot_Limit_Omaha',
            track: 'Pot Limit Omaha'
          },
          value: 'omaha.pl'
        }
      ]
    }
  ],
  title: { defaultMessage: 'Game', id: 'TXTCLI_Game', track: 'Game' }
};

export const mappedFilterBuyIn = {
  name: FilterName.BuyIn,
  hasGroups: false,
  options: [
    {
      id: 'buy-in-low',
      label: { defaultMessage: 'Low', id: 'TXTCLI_Low', track: 'Low' },
      value: 'low'
    },
    {
      id: 'buy-in-medium',
      label: { defaultMessage: 'Medium', id: 'TXTCLI_Medium', track: 'Medium' },
      value: 'medium'
    }
  ],
  title: { defaultMessage: 'Buy-In', id: 'TXTCLI_Buy_In', track: 'Buy-In' }
};

export const mappedFilterTableSize = {
  name: FilterName.TableSize,
  options: [
    {
      id: 'table-size-normal',
      label: {
        id: 'TXTCLI_7_10_Players',
        defaultMessage: '7-10 Players',
        track: '7-10 Players'
      },
      value: 'normal'
    },
    {
      id: 'table-size-short',
      label: {
        id: 'TXTCLI_3_6_Players',
        defaultMessage: '3-6 Players',
        track: '3-6 Players'
      },
      value: 'short'
    }
  ],
  title: {
    id: 'TXTCLI_Table_Size',
    defaultMessage: 'Table Size',
    track: 'Table Size'
  }
};

export const mappedFilterSpeed = {
  name: FilterName.Speed,
  options: [
    {
      id: 'speed-normal',
      label: {
        id: 'TXTCLI_Regular',
        defaultMessage: 'Regular',
        track: 'Regular'
      },
      value: 'normal'
    },
    {
      id: 'speed-turbo',
      label: {
        id: 'TXTHHY_X040Turbo',
        defaultMessage: ' Turbo',
        track: ' Turbo'
      },
      value: 'turbo'
    }
  ],
  title: {
    id: 'TXTCLI_Speed',
    defaultMessage: 'Speed',
    track: 'Speed'
  }
};

export const mappedFiltersDataCOM = { PM: [], RM: [mappedFilterGames, mappedFilterBuyIn] };

export const mappedFiltersDataIT = {
  RM: [
    {
      name: FilterName.Games,
      hasGroups: true,
      options: [
        {
          id: 'games-holdem',
          label: {
            defaultMessage: "Hold''em Games",
            id: 'TXTCLI_HoldXem_Games',
            track: "Hold''em Games"
          },
          options: [
            {
              id: 'games-sixplus-holdem',
              label: {
                defaultMessage: "6+ Hold''em",
                id: 'TXTCLI_6X_HoldXem',
                track: "6+ Hold''em"
              },
              value: 'sixplus-holdem'
            },
            {
              id: 'games-tempest-holdem',
              label: {
                defaultMessage: "Tempest Hold''em",
                id: 'TXTHHY_Tempest_HoldXem',
                track: "Tempest Hold''em"
              },
              value: 'tempest-holdem'
            }
          ]
        },
        {
          id: 'games-omaha',
          label: {
            defaultMessage: 'Omaha Games',
            id: 'TXTCLI_Omaha_Games'
          },
          options: [
            {
              id: 'games-omaha6',
              label: {
                defaultMessage: '6 Card Omaha',
                id: 'TXTHHY_6_Card_Omaha',
                track: '6 Card Omaha'
              },
              value: 'omaha6'
            },
            {
              id: 'games-omaha5',
              label: {
                defaultMessage: '5 Card Omaha',
                id: 'TXTCLI_5_Card_Omaha',
                track: '5 Card Omaha'
              },
              value: 'omaha5'
            }
          ]
        }
      ],
      title: { defaultMessage: 'Game', id: 'TXTCLI_Game', track: 'Game' }
    },
    {
      name: FilterName.BuyIn,
      hasGroups: false,
      options: [
        {
          id: 'buy-in-freeroll',
          label: { defaultMessage: 'Freeroll', id: 'TXTSRV_Freeroll', track: 'Freeroll' },
          value: 'freeroll'
        },
        {
          id: 'buy-in-micro',
          label: { defaultMessage: 'Micro', id: 'TXTCLI_Micro', track: 'Micro' },
          value: 'micro'
        }
      ],
      title: { defaultMessage: 'Buy-In', id: 'TXTCLI_Buy_In', track: 'Buy-In' }
    }
  ],
  PM: []
};

export const filtersDataEmpty = { RM: [], PM: [] };

export const filtersResponseMock: FiltersConfig[] = [
  {
    criteria: 'site:com,eu,comga;platform:web',
    rm: {
      cash: {
        main: [],
        more: []
      },
      zoom: { main: [], more: [] },
      sng: { main: [], more: [] },
      tourney: {
        main: [
          {
            name: FilterName.Games,
            sections: [
              { name: 'holdem', options: ['holdem.nl'] },
              { name: 'omaha', options: ['omaha.pl'] },
              { name: 'stud', options: [] }
            ]
          },
          { name: FilterName.BuyIn, options: ['low', 'medium'] }
        ],
        more: [
          {
            name: 'format',
            options: ['regular', 'rebuy', 're-entry', 'knockout', 'zoom']
          }
        ]
      },
      events: { main: [], more: [] }
    },
    pm: {
      cash: { main: [], more: [] },
      zoom: { main: [], more: [] },
      sng: { main: [], more: [] },
      tourney: { main: [], more: [] },
      events: { main: [], more: [] }
    }
  },
  {
    criteria: 'site:it,fr;platform:web',
    rm: {
      cash: {
        main: [],
        more: []
      },
      zoom: { main: [], more: [] },
      sng: { main: [], more: [] },
      tourney: {
        main: [
          {
            name: FilterName.Games,
            sections: [
              { name: 'holdem', options: ['sixplus-holdem', 'tempest-holdem'] },
              { name: 'omaha', options: ['omaha6', 'omaha5'] }
            ]
          },
          { name: FilterName.BuyIn, options: ['freeroll', 'micro'] }
        ],
        more: [
          {
            name: 'currency',
            options: ['usd', 'eur', 'gpb', 'cad', 'inr']
          }
        ]
      },
      events: { main: [], more: [] }
    },
    pm: {
      cash: { main: [], more: [] },
      zoom: { main: [], more: [] },
      sng: { main: [], more: [] },
      tourney: { main: [], more: [] },
      events: { main: [], more: [] }
    }
  }
];
